import { type FC, useEffect, useRef, useState } from 'react'

import classNames from 'classnames'
import { useTranslation } from 'react-i18next'

import {
  IconPollAsk,
  IconPollBgImg,
  IconPollGradient,
  IconPollYesNo,
  IconStatsClose
} from 'assets/svg/sprite'
import { Button } from 'components/Button/Button'
import BackgroundPicker, {
  DEFAULT_STORY_BACKGROUND,
  type StoryBackgroundKey,
  getStoryBackground
} from 'components/UI/BackgroundPicker/BackgroundPicker'
import Sidebar from 'components/UI/Sidebar/Sidebar'
import SidebarItem from 'components/UI/Sidebar/SidebarItem'
import { useHeaderLayout } from 'context/headerLayoutContext'
import { useOrientation } from 'helpers/helperHooks'
import { getImageOrientation } from 'helpers/util'
import { attachPhotoFromS3, presignPhoto, uploadFileToS3 } from 'services/endpoints/attachment'
import { type IPoll, type PollType } from 'types/interfaces/ITypes'

import AskQuestion from './AskQuestion'
import Poll from './Poll'
import YesNoQuestion from './YesNoQuestion'
import './_addPoll.scss'


interface Props {
  handleClose: () => void
  handleSetPollData: (data: IPoll) => void
  noControls?: boolean
  backgroundColor?: string
  initialBackgroundImage?: string | null
  initialBackgroundFile?: File | null
  initialBackgroundUploadedSrc?: string | null
  initialBackgroundOrientation?: string
  hideAskOption?: boolean
}

interface IPollOptions {
  a: string
  b: string
  c?: string
  d?: string
}

// If you can’t edit IPoll right now, we’ll pass extra fields via `as any`.
type PollWithBgSrc = IPoll & { pollBgUploadedSrc?: string | null }

const AddPoll: FC<Props> = ({
  handleClose,
  handleSetPollData,
  noControls,
  backgroundColor,
  initialBackgroundImage,
  initialBackgroundFile,
  initialBackgroundUploadedSrc,
  initialBackgroundOrientation,
  hideAskOption = false,
}) => {

const [bgColor, setBgColor] = useState<StoryBackgroundKey>(
  (backgroundColor as StoryBackgroundKey) ?? DEFAULT_STORY_BACKGROUND
)
const [bgImg, setBgImg] = useState<string | null>(initialBackgroundImage ?? null)
const [bgImgFile, setBgImgFile] = useState<File | null>(initialBackgroundFile ?? null)
const [bgImgOrientation, setBgImgOrientation] = useState<string>(initialBackgroundOrientation ?? '')
const [bgUploading, setBgUploading] = useState(false)
const [bgUploadError, setBgUploadError] = useState<string>('')
const [bgUploadedSrc, setBgUploadedSrc] = useState<string | null>(initialBackgroundUploadedSrc ?? null)


  // used to ignore late async results when user picks a new image quickly
  const uploadTokenRef = useRef(0)

  const [selectedPollType, setSelectedPollType] = useState<PollType>('poll')
  const [yesNoQuestion, setYesNoQuestion] = useState('')
  const [askQuestion, setAskQuestion] = useState('')
  const [pollTitle, setPollTitle] = useState('')
  const [pollOptions, setPollOptions] = useState<IPollOptions>({
    a: '',
    b: ''
  })
  const [validationError, setValidationError] = useState('')
  const { setHideFooter } = useHeaderLayout()
  const pollImgRef = useRef<HTMLInputElement>(null)
  const deviceOrientation = useOrientation()
  const { t } = useTranslation()

  useEffect(() => {
    setHideFooter(true)
    return () => setHideFooter(false)
  }, [setHideFooter])

  const resetBg = () => {
    setBgImg(null)
    setBgImgFile(null)
    setBgImgOrientation('')
    setBgUploadedSrc(null)
    setBgUploading(false)
    setBgUploadError('')
    uploadTokenRef.current += 1 // invalidate any in-flight upload
  }

  const handleImgUpload = async (e: React.FormEvent<HTMLInputElement>) => {
    const input = e.currentTarget
    const file = input.files?.[0] ?? null
    if (!file) return

    // allow selecting same file again later
    input.value = ''

    // new selection invalidates previous upload results
    const myToken = ++uploadTokenRef.current

    // show preview ASAP
    setBgUploadError('')
    setBgImgFile(file)
    setBgUploadedSrc(null)
    setBgUploading(true)

    // Preview: dataURL (your current UI relies on this)
    const reader = new FileReader()
    reader.onload = async () => {
      const imgDataUrl = reader.result as string
      setBgImg(imgDataUrl)

      try {
        const orientation = await getImageOrientation(imgDataUrl)
        if (orientation) setBgImgOrientation(orientation)
      } catch {
        // ignore orientation failure
      }
    }
    reader.readAsDataURL(file)

    // Upload in background: presign -> PUT -> attach confirm
    try {
      // 1) presign
      const { url: putUrl, key } = await presignPhoto({
        type: 'post_image', // ideally 'poll_bg' if your BE supports; use what presign expects
        contentType: file.type,
        size: file.size
      })

      // user picked another file while waiting
      if (uploadTokenRef.current !== myToken) return

      // 2) put
      await uploadFileToS3(putUrl, file, () => {
        // you can add progress if you want, but not required
      })

      if (uploadTokenRef.current !== myToken) return

      // 3) confirm/attach
      const attach = await attachPhotoFromS3({
        s3_key: key,
        type: 'post'
      })

      if (uploadTokenRef.current !== myToken) return

      const resData = (attach as any)?.data?.data ?? (attach as any)?.data ?? attach
      const src = resData?.storage_path ?? resData?.path ?? key

      setBgUploadedSrc(src)
      setBgUploading(false)
      setBgUploadError('')
    } catch (err: any) {
      console.error('[AddPoll] bg upload failed', err)
      if (uploadTokenRef.current !== myToken) return

      setBgUploading(false)
      setBgUploadedSrc(null)
      setBgUploadError(t('error:errorSomethingWentWrong'))
      // keep preview visible; user can re-upload or continue with gradient
    }
  }

  const validatePoll = (title: string, options: IPollOptions, cb: () => void) => {
    const optionsWithData = Object.values(options).filter(option => option?.trim().length)
    if (!title) {
      setValidationError(t('validation:pollTitleIsRequired'))
      setTimeout(() => setValidationError(''), 5000)
    } else if (!title.trim().length) {
      setValidationError(t('validation:pollTitleCannotBeEmpty'))
      setTimeout(() => setValidationError(''), 5000)
    } else if (optionsWithData.length < 2) {
      setValidationError(t('validation:pollMustHaveMinimum2Options'))
      setTimeout(() => setValidationError(''), 5000)
    } else {
      cb()
    }
  }

  const validateQuestion = (value: string, cb: () => void) => {
    if (!value?.trim().length) {
      setValidationError(t('validation:questionIsRequired'))
      setTimeout(() => setValidationError(''), 5000)
    } else {
      cb()
    }
  }

  const handleSavePoll = () => {
    // If user selected an image, require upload to have finished successfully.
    const hasSelectedImage = !!bgImgFile
    const imageReady = !hasSelectedImage || (!!bgUploadedSrc && !bgUploading && !bgUploadError)

    if (!imageReady) {
      setValidationError(t('error:errorSomethingWentWrong'))
      setTimeout(() => setValidationError(''), 5000)
      return
    }

    const base: PollWithBgSrc = {
      type: selectedPollType,
      question: '',
      pollBg: bgImg ?? bgColor,
      pollBgImg: bgImgFile, // keep for backwards compat if someone still reads it, but NewPost won’t need it
      pollBgUploadedSrc: bgUploadedSrc,
      answers: null
    }

    switch (selectedPollType) {
      case 'poll':
        validatePoll(pollTitle, pollOptions, () => {
          const trimedTitle = pollTitle.trim()
          const trimedOptions = { ...pollOptions }
          // note: value.trim() here didn’t mutate; keep as-is or adjust if needed
          const answers = Object.values(trimedOptions).filter(el => el)

          handleSetPollData({
            ...(base as any),
            type: 'poll',
            question: trimedTitle,
            answers
          } satisfies IPoll)
          handleClose()
        })
        break

      case 'yes/no':
        validateQuestion(yesNoQuestion, () => {
          handleSetPollData({
            ...(base as any),
            type: 'yes/no',
            question: yesNoQuestion,
            answers: null
          } satisfies IPoll)
          handleClose()
        })
        break

      case 'ask':
        validateQuestion(askQuestion, () => {
          handleSetPollData({
            ...(base as any),
            type: 'ask',
            question: askQuestion,
            answers: null
          } satisfies IPoll)
          handleClose()
        })
        break

      default:
        handleClose()

    }
  }

  if (!deviceOrientation) return null

  const hasImageSelected = !!bgImgFile
  const canSaveImageState =
    !hasImageSelected || (!!bgUploadedSrc && !bgUploading && !bgUploadError)

  const disableSave =
    bgUploading ||
    !canSaveImageState ||
    (selectedPollType === 'poll' && (
      !pollTitle.trim().length ||
      Object.values(pollOptions).filter(value => value.trim() !== '').length < 2
    )) ||
    (selectedPollType === 'ask' &&
      !askQuestion.trim().length) ||
    (selectedPollType === 'yes/no' &&
      !yesNoQuestion.trim().length)

const handleAddPollOption = () => {
  setPollOptions(prev => {
    if (!Object.prototype.hasOwnProperty.call(prev, 'c')) {
      return { ...prev, c: '' }
    }

    if (!Object.prototype.hasOwnProperty.call(prev, 'd')) {
      return { ...prev, d: '' }
    }

    return prev
  })
}

const handleRemovePollOption = (key: 'c' | 'd') => {
  setPollOptions(prev => {
    const updated = { ...prev }

    if (key === 'c') {
      if (Object.prototype.hasOwnProperty.call(updated, 'd')) {
        updated.c = updated.d
        delete updated.d
      } else {
        delete updated.c
      }

      return updated
    }

    delete updated.d
    return updated
  })
}


return (
    <div
        className={classNames('addPoll', {
            'addPoll--withBgImg': !!bgImg,
            'device-portrait': deviceOrientation.type.includes('portrait'),
            'device-landscape': deviceOrientation.type.includes('landscape'),
            'img-portrait': bgImgOrientation === 'portrait',
            'img-landscape': bgImgOrientation === 'landscape'
        })}
        style={{
            backgroundImage: bgImg
                ? `url(${bgImg})`
                : getStoryBackground(bgColor)
        }}
    >
        {bgImg && (
            <div
                className='addPoll__bg'
                style={{ backgroundImage: `url(${bgImg})` }}
            />
        )}

        <div className='addPoll__wrapper'>
        <div className='addPoll__header'>
          <div className='addPoll__header__title'>
            {selectedPollType === 'ask'
              ? 'Ask Your Fans'
              : t('addPoll')}
          </div>
          <div className='add__header__close' onClick={handleClose}>
            <IconStatsClose />
          </div>
        </div>

        <div className='addPoll__tabs'>
          <div
            className={`addPoll__tab addPoll__tab--${selectedPollType === 'poll' ? 'active' : ''}`}
            onClick={() => {
              if (selectedPollType !== 'poll') {
                setSelectedPollType('poll')
                validationError && setValidationError('')
              }
            }}
          >
            <div className='addPoll__tab__icon'><IconPollGradient /></div>
            <div className='addPoll__tab__text'>{t('poll')}</div>
          </div>

          <div
            className={`addPoll__tab addPoll__tab--${selectedPollType === 'yes/no' ? 'active' : ''}`}
            onClick={() => {
              if (selectedPollType !== 'yes/no') {
                setSelectedPollType('yes/no')
                validationError && setValidationError('')
              }
            }}
          >
            <div className='addPoll__tab__icon'><IconPollYesNo /></div>
            <div className='addPoll__tab__text'>{t('yesNo')}</div>
          </div>
          {!hideAskOption && (
          <div
            className={`addPoll__tab addPoll__tab--${selectedPollType === 'ask' ? 'active' : ''}`}
            onClick={() => {
              if (selectedPollType !== 'ask') {
                setSelectedPollType('ask')
                validationError && setValidationError('')
              }
            }}
          >
            <div className='addPoll__tab__icon'><IconPollAsk /></div>
            <div className='addPoll__tab__text'>{t('ask')}</div>
          </div>
          )}
        </div>
          
        {!noControls && (
          <Sidebar>
            <SidebarItem
              icon={<IconPollBgImg />}
              text={t('upload')}
              onClick={() => pollImgRef.current?.click()}
            >
              <input
                ref={pollImgRef}
                type='file'
                onChange={handleImgUpload}
                id='poll__img__upload'
                accept='image/*'
                hidden
              />
            </SidebarItem>

            {/* optional: add a “remove image” action if you want */}
            {bgImg && (
              <SidebarItem
                icon={<IconPollBgImg />}
                text={'Remove'}
                onClick={resetBg}
              />
            )}

            <BackgroundPicker background={bgColor} setBackground={setBgColor} />
          </Sidebar>
        )}

        <div className='addPoll__content'>

          {selectedPollType === 'poll' && (
            <Poll
              title={pollTitle}
              setPollTitle={(title: string) => setPollTitle(title)}
              options={pollOptions}
              handleOptionChange={(key: string, value: string) => {
                setPollOptions({ ...pollOptions, [key]: value })
              }}
              handleAddOption={handleAddPollOption}
              handleRemoveOption={handleRemovePollOption}
              validationError={validationError}
            />
          )}

          {selectedPollType === 'yes/no' && (
            <YesNoQuestion
              question={yesNoQuestion}
              handleQuestionChange={(value: string) => setYesNoQuestion(value)}
              validationError={validationError}
            />
          )}

          {selectedPollType === 'ask' && (
            <AskQuestion
              question={askQuestion}
              handleQuestionChange={(value: string) => setAskQuestion(value)}
              validationError={validationError}
            />
          )}
        </div>

        <div className='addPoll__footer'>
          <Button
            text={t('cancel')}
            color='white'
            font='mont-14-normal'
            width='12'
            height='3'
            clickFn={handleClose}
          />
          <Button
            text={t('save')}
            color='black'
            font='mont-14-normal'
            width='13'
            height='3'
            clickFn={handleSavePoll}
            disabled={disableSave}
          />
        </div>
      </div>
    </div>
  )
}

export default AddPoll
